import React, { useState, useEffect } from 'react';
import { Crown, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const serviceIcons = {
    'infusion-beauty': '✨',
    'hydrate': '💧',
    'athletic-drip': '💪',
    'myers-cocktail': '🏆',
    'infusion-migraine': '🧠',
    'fat-burner-drip': '🔥',
    'immune-drip': '🛡️'
  };

  // Price map by service id — mirrors HomePage source of truth
  const servicePrices = {
    'myers-cocktail': 210,
    'hangover-drip': 210,
    'hydrate': 135,
    'athletic-drip': 210,
    'infusion-beauty': 210,
    'immune-drip': 210,
    'infusion-migraine': 210,
  };

  const categoryColors = {
    'beauty': 'from-pink-500/20 to-purple-500/20',
    'wellness': 'from-green-500/20 to-emerald-500/20',
    'performance': 'from-orange-500/20 to-red-500/20',
    'relief': 'from-blue-500/20 to-cyan-500/20',
    'immunity': 'from-yellow-500/20 to-yellow-600/20'
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Crown className="w-16 h-16 text-yellow-400 pulse-gold" />
          </div>
          <div className="inline-block text-yellow-400/80 text-xs uppercase tracking-[0.4em] mb-4 font-gothic">
            The EliteIV Menu
          </div>
          <h1 className="text-5xl md:text-6xl font-gothic mb-6">
            <span className="gradient-text">Our IV Therapy Menu</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto font-gothic-italic">
            Premium vitamin infusions tailored to your wellness goals. All treatments include professional RN administration and patient education.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 px-4" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center text-gray-400">Loading services...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className="gothic-card"
                  data-testid={`service-detail-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="gothic-icon" style={{ width: 64, height: 64, fontSize: 32 }}>
                      {serviceIcons[service.id] || '💉'}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="bg-yellow-400/10 text-yellow-400 text-[10px] font-gothic font-semibold px-3 py-1 rounded-full uppercase tracking-[0.2em] border border-yellow-400/30">
                        {service.category}
                      </span>
                      {servicePrices[service.id] && (
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-400/60 font-gothic">From</div>
                          <div className="text-3xl font-gothic text-yellow-400 font-semibold leading-none">
                            ${servicePrices[service.id]}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <h3 className="gothic-heading text-3xl">{service.name}</h3>
                  <div className="gothic-divider" />
                  <p className="gothic-body text-base mb-6 leading-relaxed">{service.description}</p>

                  <div className="border-t border-yellow-500/15 pt-5 space-y-3 mb-6">
                    {[
                      '100% Nutrient Absorption',
                      '30–45 Minute Session',
                      'Mobile Service Available',
                      'Licensed RN Administered',
                    ].map((feature) => (
                      <div key={feature} className="flex items-center space-x-2 text-gray-300">
                        <Check className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <span className="font-gothic-italic">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setShowBooking(true)}
                    className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-gothic font-bold py-3 rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all tracking-[0.1em] uppercase text-sm"
                    data-testid={`book-service-${index}-btn`}
                  >
                    Book This Treatment
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Info */}
      <section className="py-16 px-4 bg-gray-900/50" data-testid="pricing-info">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            <span className="gradient-text">Pricing Information</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Every body is unique, and so are your wellness needs. We provide personalized pricing based on your specific requirements and treatment plan.
          </p>
          <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-600/10 border-2 border-yellow-400/30 rounded-2xl p-8">
            <p className="text-lg text-gray-300 mb-6">
              📞 Call us for a free consultation and personalized quote
            </p>
            <a
              href="tel:727-503-2572"
              className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold px-8 py-4 rounded-full text-lg hover:from-yellow-500 hover:to-yellow-700 transition-all"
              data-testid="call-for-pricing-btn"
            >
              Call 727-503-2572
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">How It Works</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-yellow-400/20 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-yellow-400 mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Book</h3>
              <p className="text-gray-300">Call or book online to schedule your session</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-400/20 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-yellow-400 mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold text-yellow-400 mb-2">We Come</h3>
              <p className="text-gray-300">Our RN arrives at your location with everything needed</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-400/20 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-yellow-400 mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Relax</h3>
              <p className="text-gray-300">Sit back and relax during your 30-45 minute treatment</p>
            </div>
            <div className="text-center">
              <div className="bg-yellow-400/20 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold text-yellow-400 mx-auto mb-4">4</div>
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Feel Amazing</h3>
              <p className="text-gray-300">Experience the benefits and feel your best</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Ready to Experience Elite Care?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book your IV therapy session today and feel the difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowBooking(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-full text-lg font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all"
              data-testid="services-book-now-btn"
            >
              Book Now
            </button>
            <a
              href="tel:727-503-2572"
              className="border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-full text-lg font-bold hover:bg-yellow-400 hover:text-black transition-all"
              data-testid="services-call-btn"
            >
              Call 727-503-2572
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </div>
  );
};

export default ServicesPage;